import { ChangeEvent } from 'react';
import { FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon } from '@mui/icons-material';

import { Agency } from '../../../types/Agency';
import { USER_GROUP_ADMIN } from '../../../types/User';
import AccessGuard from '../../../components/AccessGuard';
import FormSection from '../../../components/FormSection';
import Row from '../../../components/Row';

const AdminOnlyActionIcon = () => {
    return (
        <Tooltip title="Visible to Admins Only">
            <AdminPanelSettingsOutlinedIcon color="primary" fontSize="small" />
        </Tooltip>
    );
};

const AdminOnlyFormControlLabel = ({ label }: { label: string }) => {
    return (
        <FormControlLabel
            control={
                <Tooltip title={label}>
                    <Switch disabled={true} checked={true} />
                </Tooltip>
            }
            label={
                <Row gap={1} alignItems="center">
                    {label}
                    <AdminOnlyActionIcon />
                </Row>
            }
        />
    );
};

interface AccessPermissionsProps {
    agency: Agency;
    setAgency: (agency: Agency) => void;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const AccessPermissions = (props: AccessPermissionsProps) => {
    const { agency, onChange } = props;

    return (
        <>
            <Grid container spacing={3}>
                <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                    <>
                        <Grid size={12}>
                            <Grid>
                                <FormSection>
                                    <Typography variant="h6">Media Studio</Typography>
                                    <Grid container rowSpacing={1} columnSpacing={2}>
                                        <Grid size={6}>
                                            <FormControlLabel
                                                control={
                                                    <Tooltip title="Create Campaigns">
                                                        <Switch
                                                            checked={agency.activeCampaigns}
                                                            onChange={onChange}
                                                            name="activeCampaigns"
                                                        />
                                                    </Tooltip>
                                                }
                                                label="Create Campaigns"
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <FormControlLabel
                                                control={
                                                    <Tooltip title="Campaigns">
                                                        <Switch
                                                            checked={agency.activeCampaigns}
                                                            onChange={onChange}
                                                            name="activeCampaigns"
                                                        />
                                                    </Tooltip>
                                                }
                                                label="Campaigns"
                                            />
                                        </Grid>
                                    </Grid>
                                </FormSection>
                            </Grid>
                        </Grid>
                        <Grid size={12}>
                            <Grid>
                                <FormSection>
                                    <Typography variant="h6">Measurement Studio</Typography>
                                    <Grid container rowSpacing={1} columnSpacing={2}>
                                        <Grid size={3}>
                                            <FormControlLabel
                                                control={
                                                    <Tooltip title="Reporting">
                                                        <Switch
                                                            checked={agency.activeReporting}
                                                            onChange={onChange}
                                                            name="activeReporting"
                                                        />
                                                    </Tooltip>
                                                }
                                                label="Reporting"
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <FormControlLabel
                                                control={
                                                    <Tooltip title="Attribution">
                                                        <Switch
                                                            checked={agency.activeAttribution}
                                                            onChange={onChange}
                                                            name="activeAttribution"
                                                        />
                                                    </Tooltip>
                                                }
                                                label="Attribution"
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <FormControlLabel
                                                control={
                                                    <Tooltip title="Headless Analytics Tag (HAT)">
                                                        <Switch
                                                            checked={agency.tfUserIsActive}
                                                            onChange={onChange}
                                                            name="tfUserIsActive"
                                                        />
                                                    </Tooltip>
                                                }
                                                label="Headless Analytics Tag (HAT)"
                                            />
                                        </Grid>
                                    </Grid>
                                </FormSection>
                            </Grid>
                        </Grid>
                        <Grid size={12}>
                            <FormSection>
                                <Typography variant="h6">Data Studio</Typography>
                                <Grid container rowSpacing={1} columnSpacing={2}>
                                    <Grid size={3}>
                                        <FormControlLabel
                                            control={
                                                <Tooltip title="First Party Data Upload">
                                                    <Switch
                                                        checked={agency.activeCrmUpload}
                                                        onChange={onChange}
                                                        name="activeCrmUpload"
                                                    />
                                                </Tooltip>
                                            }
                                            label="First Party Data Upload"
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <AdminOnlyFormControlLabel label="Import Manager" />
                                    </Grid>
                                    <Grid size={3}>
                                        <AdminOnlyFormControlLabel label="Data Mapping" />
                                    </Grid>
                                    <Grid size={3}>
                                        <AdminOnlyFormControlLabel label="Amazon Marketing Cloud" />
                                    </Grid>
                                    <Grid size={12}>
                                        <AdminOnlyFormControlLabel label="DSP Audiences" />
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid size={12}>
                            <FormSection>
                                <Typography variant="h6">Ad Serving</Typography>
                                <AdminOnlyFormControlLabel label="" />
                            </FormSection>
                        </Grid>
                        <Grid size={12}>
                            <FormSection>
                                <Typography variant="h6">Creative Studio</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={agency.accessCreativeStudio}
                                            onChange={onChange}
                                            name="accessCreativeStudio"
                                        />
                                    }
                                    label=""
                                />
                            </FormSection>
                        </Grid>
                        <Grid size={12}>
                            <FormSection>
                                <Typography variant="h6">Planning & Insights</Typography>
                                <FormControlLabel
                                    control={
                                        <Tooltip title="Insights">
                                            <Switch
                                                checked={agency.activeInsights}
                                                onChange={onChange}
                                                name="activeInsights"
                                            />
                                        </Tooltip>
                                    }
                                    label=""
                                />
                            </FormSection>
                        </Grid>
                        <Grid size={12}>
                            <FormSection>
                                <Typography variant="h6">Settings</Typography>
                                <Grid container rowSpacing={1} columnSpacing={2}>
                                    <Grid size={3}>
                                        <FormControlLabel
                                            control={
                                                <Tooltip title="Advertisers">
                                                    <Switch
                                                        checked={agency.activeAdvertisers}
                                                        onChange={onChange}
                                                        name="activeAdvertisers"
                                                    />
                                                </Tooltip>
                                            }
                                            label="Advertisers"
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <AdminOnlyFormControlLabel label="Agencies" />
                                    </Grid>
                                    <Grid size={3}>
                                        <AdminOnlyFormControlLabel label="User Management" />
                                    </Grid>
                                    <Grid size={3}>
                                        <AdminOnlyFormControlLabel label="Analytics" />
                                    </Grid>
                                    <Grid size={12}>
                                        <AdminOnlyFormControlLabel label="Release Notes" />
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </>
                </AccessGuard>
            </Grid>
        </>
    );
};

export default AccessPermissions;
